import React, { useState, useEffect } from 'react';
import { Search, Award, Plus, X, Loader2, Edit2, Trash2, FileText, ExternalLink, Calendar, Link as LinkIcon } from 'lucide-react';

export default function ResultManagement() {
  const [resultsList, setResultsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Cascading Dropdown Master Collections
  const [courseCascades, setCourseCascades] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Modal State Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  // Form State
  const [resultForm, setResultForm] = useState({
    course_id: '',
    term_label: '',
    result_title: '',
    publish_date: '',
    external_portal_link: '',
    academic_year: '2026-2027',
    pdf_file_url: ''
  });

  useEffect(() => {
    fetchResults();
    fetchCourseCascades();
  }, [searchTerm]);

  const fetchResults = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/results?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) setResultsList(result.data);
      else setResultsList([]);
    } catch (err) {
      console.error(err);
      setResultsList([]);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchCourseCascades = async () => {
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/results/course-cascades`);
      const result = await res.json();
      if (result.success) setCourseCascades(result.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // 🔄 CASCADING HANDLER: Course Selection Change
  const handleCourseChange = (courseId) => {
    const selectedCourse = courseCascades.find(c => String(c.id) === String(courseId));
    
    if (selectedCourse && selectedCourse.syllabus_structure) {
      setAvailableTerms(selectedCourse.syllabus_structure);
    } else {
      setAvailableTerms([]);
    }

    setResultForm(prev => ({
      ...prev,
      course_id: courseId,
      term_label: ''
    }));
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setPdfFile(null);
    setAvailableTerms([]);
    setResultForm({
      course_id: '',
      term_label: '',
      result_title: '',
      publish_date: new Date().toISOString().substring(0, 10),
      external_portal_link: '',
      academic_year: '2026-2027',
      pdf_file_url: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setPdfFile(null);

    // Populate Course Cascades
    const selectedCourse = courseCascades.find(c => String(c.id) === String(item.course_id));
    let terms = [];

    if (selectedCourse && selectedCourse.syllabus_structure) {
      terms = selectedCourse.syllabus_structure;
    }

    setAvailableTerms(terms);

    const formattedDate = item.publish_date ? item.publish_date.substring(0, 10) : '';

    setResultForm({
      course_id: item.course_id,
      term_label: item.term_label,
      result_title: item.result_title,
      publish_date: formattedDate,
      external_portal_link: item.external_portal_link || '',
      academic_year: item.academic_year || '2026-2027',
      pdf_file_url: item.pdf_file_url || ''
    });

    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('payload', JSON.stringify(resultForm));
    if (pdfFile) {
      formData.append('result_pdf', pdfFile);
    }

    const endpoint = editingId ? `update-result/${editingId}` : 'add-result';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/results/${endpoint}`, {
        method: method,
        body: formData
      });
      const result = await res.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchResults();
      } else {
        alert(result.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this result record?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/results/delete-result/${id}`, {
        method: 'PUT'
      });
      if ((await res.json()).success) fetchResults();
    } catch (err) {
      console.error(err);
    }
  };

  // Pagination Range Slices
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = resultsList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(resultsList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP HEADER CTA BAR */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Examination Results & Scorecards</h2>
          <p style={styles.mainSubtitle}>Publish term-wise result gazettes, PDF scorecards, and external result checking links</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Publish New Result
        </button>
      </div>

      {/* SEARCH AND RECORD COUNT STRIP */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by result title, term, or course..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>Published Results: <strong style={{ color: '#1c6ec9' }}>{resultsList.length}</strong></div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.tableTh}>Course Stream</th>
                <th style={styles.tableTh}>Term & Result Title</th>
                <th style={styles.tableTh}>Publish Date</th>
                <th style={styles.tableTh}>PDF Scorecard Document</th>
                <th style={styles.tableTh}>Portal Verification Link</th>
                <th style={styles.tableTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resultsList.length === 0 ? (
                <tr><td colSpan="6" style={styles.noDataTd}>No examination results published yet.</td></tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={styles.tableTr}>
                    <td style={styles.tableTd}>
                      <strong style={{ color: '#1c6ec9', fontSize: '13.5px', display: 'block' }}>{item.course_name}</strong>
                      <span style={styles.codeTag}>Code: {item.course_code}</span>
                    </td>
                    <td style={styles.tableTd}>
                      <span style={styles.termBadge}>{item.term_label}</span>
                      <div style={{ fontWeight: '600', color: '#0f172a', marginTop: '4px' }}>{item.result_title}</div>
                      <small style={{ color: '#64748b' }}>Academic Year: {item.academic_year}</small>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '600', color: '#334155' }}>
                        <Calendar size={13} color="#64748b" />
                        {new Date(item.publish_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      {item.pdf_file_url ? (
                        <a href={item.pdf_file_url} target="_blank" rel="noreferrer" style={styles.fileAnchor}>
                          <FileText size={11} style={{ marginRight: '3px' }} /> Download PDF Gazette <ExternalLink size={10} style={{ marginLeft: '2px' }} />
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>No PDF Attached</span>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      {item.external_portal_link ? (
                        <a href={item.external_portal_link} target="_blank" rel="noreferrer" style={styles.portalAnchor}>
                          <LinkIcon size={11} style={{ marginRight: '3px' }} /> Check Live Result <ExternalLink size={10} style={{ marginLeft: '2px' }} />
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>Offline / Internal Only</span>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.actionBtnGroup}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit Result"><Edit2 size={12} /> Edit</button>
                        <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete Result"><Trash2 size={12} /> Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {resultsList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* POPUP MANAGEMENT MODAL */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                {editingId ? 'Modify Examination Result Entry' : 'Publish Examination Result'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              {/* STEP 1: SELECT COURSE */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Select Course Stream *</label>
                <select
                  required
                  value={resultForm.course_id}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  style={styles.formSelect}
                >
                  <option value="">-- Choose Target Course --</option>
                  {courseCascades.map(c => (
                    <option key={c.id} value={c.id}>{c.course_code} - {c.course_name}</option>
                  ))}
                </select>
              </div>

              {/* STEP 2: SELECT TERM / SEMESTER & ACADEMIC YEAR */}
              <div style={styles.formSplitGrid}>
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Select Term / Semester *</label>
                  <select
                    required
                    disabled={!resultForm.course_id}
                    value={resultForm.term_label}
                    onChange={(e) => setResultForm({ ...resultForm, term_label: e.target.value })}
                    style={styles.formSelect}
                  >
                    <option value="">-- Select Term --</option>
                    {availableTerms.map((t, idx) => (
                      <option key={idx} value={t.term}>{t.term}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Academic Year Framework</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 2026-2027"
                    value={resultForm.academic_year}
                    onChange={(e) => setResultForm({ ...resultForm, academic_year: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
              </div>

              {/* RESULT TITLE */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Result Title / Heading *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Semester 1 End-Term Examination Results - Dec 2026"
                  value={resultForm.result_title}
                  onChange={(e) => setResultForm({ ...resultForm, result_title: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              {/* PUBLISH DATE & EXTERNAL PORTAL LINK */}
              <div style={styles.formSplitGrid}>
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>Official Publish Date *</label>
                  <input
                    type="date"
                    required
                    value={resultForm.publish_date}
                    onChange={(e) => setResultForm({ ...resultForm, publish_date: e.target.value })}
                    style={styles.formInput}
                  />
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel}>External Verification Link (Optional)</label>
                  <input
                    type="url"
                    placeholder="https://results.university.edu/check"
                    value={resultForm.external_portal_link}
                    onChange={(e) => setResultForm({ ...resultForm, external_portal_link: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
              </div>

              {/* RESULT PDF ATTACHMENT */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Upload Result PDF Gazette Document (Optional)</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  style={styles.formFileInput}
                />
              </div>

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>Publish Result Record</button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES OBJECT MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '340px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTd: { padding: '12px 14px', color: '#475569', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  noDataTd: { padding: '30px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  codeTag: { background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontSize: '10.5px', fontWeight: '700', display: 'inline-block', marginTop: '2px' },
  termBadge: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', display: 'inline-block' },
  fileAnchor: { display: 'inline-flex', alignItems: 'center', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1c6ec9', padding: '4px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', textDecoration: 'none' },
  portalAnchor: { display: 'inline-flex', alignItems: 'center', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '4px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', textDecoration: 'none' },
  actionBtnGroup: { display: 'flex', gap: '6px', alignItems: 'center' },
  editRowBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  deleteRowBtn: { background: '#fff1f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxWidth: '580px', maxHeight: '90vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '14px' },
  formSplitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  formSelect: { padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none' },
  formFileInput: { fontSize: '12px', color: '#475569', padding: '6px 0' },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' }
};